// Centralized Google Analytics service with typed event tracking

import { createLogger } from '@airhead-games/shared/utilities';
import type {
  AnalyticsEventName,
  AnalyticsEventParams,
  AnalyticsAudience,
} from '../types/analytics';
import type { UserAuthFields } from '@airhead-games/shared/types';

const logger = createLogger('analyticsService');

// Session storage key for tracking player status (for anonymous users)
const PLAYER_STATUS_KEY = 'ga_player_status';

/**
 * Determines analytics_audience based on hierarchy: user > player
 * Returns undefined for anonymous visitors who haven't joined a room
 */
const getAnalyticsAudience = (
  user: UserAuthFields | null,
  isPlayer: boolean
): AnalyticsAudience | undefined => {
  if (user) {
    return 'user';
  }
  if (isPlayer) {
    return 'player';
  }
  return undefined;
};

/**
 * Sets player status (for anonymous users who join rooms)
 */
const setPlayerStatus = (isPlayer: boolean): void => {
  if (typeof window === 'undefined') return;

  if (isPlayer) {
    sessionStorage.setItem(PLAYER_STATUS_KEY, 'true');
  } else {
    sessionStorage.removeItem(PLAYER_STATUS_KEY);
  }
};

// Get tracking ID from environment variable
const getTrackingId = (): string | null => {
  const trackingId = import.meta.env.VITE_GA_TRACKING_ID;
  if (!trackingId) {
    logger.warn('VITE_GA_TRACKING_ID not set in environment variables');
    return null;
  }
  return trackingId;
};

// Check if GA is initialized
const isGAInitialized = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Initialize Google Analytics on app startup
 */
export const initialize = (): void => {
  const trackingId = getTrackingId();

  if (!trackingId) {
    logger.warn(
      'VITE_GA_TRACKING_ID not set. Google Analytics will not be initialized.'
    );
    return;
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = (...args) => {
      window.dataLayer.push(args);
    };
    window.gtag('js', new Date());
  }

  // Queue config immediately so early manual page views/interactions emitted
  // during app startup are processed against the intended GA property.
  window.gtag('config', trackingId, {
    send_page_view: false,
  });

  const existingScript = document.querySelector<HTMLScriptElement>(
    `script[data-ga-tracking-id="${trackingId}"]`
  );
  if (existingScript) {
    return;
  }

  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
  script.dataset.gaTrackingId = trackingId;

  // Initialize GA config after script loads
  // Disable automatic page view tracking - we'll track manually via trackPageView
  script.onload = () => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', trackingId, {
        send_page_view: false,
      });
      logger.info('Google Analytics initialized', { trackingId });
    } else {
      logger.warn('gtag function not available after script load.');
    }
  };

  script.onerror = () => {
    logger.error('Failed to load Google Analytics script');
  };

  document.head.appendChild(script);
};

/**
 * Sets user properties in Google Analytics
 * Transforms UserAuthFields to GA4 format (snake_case field names)
 */
export const setUserProperties = (user: UserAuthFields): void => {
  if (!isGAInitialized()) {
    logger.warn(
      'Google Analytics is not initialized. Cannot set user properties.'
    );
    return;
  }

  const trackingId = getTrackingId();
  if (!trackingId) return;

  try {
    const analyticsAudience = getAnalyticsAudience(user, false); // Player status handled separately for authenticated users

    // Transform user object to GA4 user properties format (snake_case)
    const properties = {
      user_type: 'customer',
      analytics_audience: analyticsAudience,
      sign_in_count: user.signInCount,
      lead_status: null, // Will be set if available from backend
    };

    // GA4 user properties are set via config with user_properties object
    // Note: send_page_view is set to false to prevent triggering automatic page views
    window.gtag('config', trackingId, {
      user_properties: properties as Record<
        string,
        string | number | boolean | null | undefined
      >,
      send_page_view: false,
    } as unknown as Record<
      string,
      string | number | boolean | null | undefined
    >);
    logger.debug('User properties set', properties);
  } catch (error) {
    logger.error('Failed to set user properties', error);
  }
};

/**
 * Sets user ID in Google Analytics
 */
export const setUserId = (userId: string | null): void => {
  if (!isGAInitialized()) {
    logger.warn('Google Analytics is not initialized. Cannot set user ID.');
    return;
  }

  const trackingId = getTrackingId();
  if (!trackingId) return;

  try {
    if (userId) {
      window.gtag('config', trackingId, {
        user_id: userId,
        send_page_view: false,
      });
      logger.debug('User ID set', { userId });
    } else {
      // Clear user ID
      window.gtag('config', trackingId, {
        user_id: null,
        send_page_view: false,
      });
      logger.debug('User ID cleared');
    }
  } catch (error) {
    logger.error('Failed to set user ID', error);
  }
};

/**
 * Tracks a page view
 */
export const trackPageView = (
  path: string,
  title: string,
  params?: Partial<AnalyticsEventParams>
): void => {
  if (!isGAInitialized()) {
    logger.warn('Google Analytics is not initialized. Cannot track page view.');
    return;
  }

  const trackingId = getTrackingId();
  if (!trackingId) return;

  try {
    // Include analytics_audience in page view if not already provided
    // This allows the analytics page tracker to pass user context
    const eventParams: AnalyticsEventParams = {
      page_path: path,
      page_title: title,
      ...params,
    };

    // If analytics_audience not provided, try to determine it from player status
    if (!eventParams.analytics_audience && typeof window !== 'undefined') {
      const isPlayer = sessionStorage.getItem(PLAYER_STATUS_KEY) === 'true';
      // For anonymous users, we can only determine player status
      // For authenticated users, analytics_audience should be set via setUserProperties
      if (isPlayer && !eventParams.user_id) {
        eventParams.analytics_audience = 'player';
      }
    }

    // Send explicit page_view event with page_title
    // Automatic page views are disabled in initialize() so this is the only page view tracked
    window.gtag('event', 'page_view', eventParams);
    logger.debug('Page view tracked', { path, title, params: eventParams });
  } catch (error) {
    logger.error('Failed to track page view', error);
  }
};

/**
 * Tracks a custom event
 */
export const trackEvent = (
  eventName: AnalyticsEventName,
  params?: AnalyticsEventParams
): void => {
  if (!isGAInitialized()) {
    logger.warn('Google Analytics is not initialized. Cannot track event.');
    return;
  }

  const trackingId = getTrackingId();
  if (!trackingId) return;

  try {
    window.gtag('event', eventName, {
      ...params,
    });
    logger.debug('Event tracked', { eventName, params });
  } catch (error) {
    logger.error('Failed to track event', error);
  }
};

/**
 * Convenience methods for specific events
 */
export const analyticsService = {
  // Conversion events
  trackSignUp: (params?: AnalyticsEventParams) => {
    trackEvent('sign_up', params);
  },

  trackSignIn: (params?: AnalyticsEventParams) => {
    trackEvent('sign_in', params);
  },

  trackCheckoutStarted: (params: AnalyticsEventParams) => {
    trackEvent('checkout_started', params);
  },

  trackCheckoutLoginRedirect: (params: AnalyticsEventParams) => {
    trackEvent('checkout_login_redirect', params);
  },

  trackCheckoutRedirected: (params: AnalyticsEventParams) => {
    trackEvent('checkout_redirected', params);
  },

  trackQuickPurchaseSucceeded: (params: AnalyticsEventParams) => {
    trackEvent('quick_purchase_succeeded', params);
  },

  trackCheckoutFailed: (params: AnalyticsEventParams) => {
    trackEvent('checkout_failed', params);
  },

  trackPurchaseCompleted: (params: AnalyticsEventParams) => {
    trackEvent('purchase_completed', params);
  },

  trackRapidReloadDialogOpened: (params: AnalyticsEventParams) => {
    trackEvent('rapid_reload_dialog_opened', params);
  },

  trackRapidReloadSaved: (params: AnalyticsEventParams) => {
    trackEvent('rapid_reload_saved', params);
  },

  trackRapidReloadSaveFailed: (params: AnalyticsEventParams) => {
    trackEvent('rapid_reload_save_failed', params);
  },

  trackRapidReloadDisabled: (params: AnalyticsEventParams) => {
    trackEvent('rapid_reload_disabled', params);
  },

  trackLoonPackSelected: (params: AnalyticsEventParams) => {
    trackEvent('loon_pack_selected', params);
  },

  // Game/Room events
  trackRoomCreated: (params: {
    game_name: string;
    room_code: string;
    [key: string]: string | number | boolean | null | undefined;
  }) => {
    trackEvent('room_created', params);
  },

  trackGameStarted: (params: {
    game_name: string;
    player_count: number;
    room_code: string;
    [key: string]: string | number | boolean | null | undefined;
  }) => {
    trackEvent('game_started', params);
  },

  trackGameEnded: (params: {
    game_name: string;
    room_code: string;
    player_count: number;
    [key: string]: string | number | boolean | null | undefined;
  }) => {
    trackEvent('game_ended', params);
  },

  trackRoomClosed: (params: {
    room_code: string;
    game_name?: string;
    [key: string]: string | number | boolean | null | undefined;
  }) => {
    trackEvent('room_closed', params);
  },

  // UI events
  trackButtonClick: (
    buttonName: string,
    additionalParams?: AnalyticsEventParams
  ) => {
    trackEvent('button_click', {
      button_name: buttonName,
      ...additionalParams,
    });
  },

  trackMenuToggle: (state: 'open' | 'closed') => {
    trackEvent('menu_toggle', {
      menu_state: state,
    });
  },

  trackMenuItemClick: (menuItem: string) => {
    trackEvent('menu_item_click', {
      menu_item: menuItem,
    });
  },

  trackNavigationMenuToggle: (
    menuName: string,
    state: 'open' | 'closed',
    additionalParams?: AnalyticsEventParams
  ) => {
    trackEvent('menu_toggle', {
      menu_name: menuName,
      menu_state: state,
      ...additionalParams,
    });
  },

  trackNavigationItemClick: (
    menuItem: string,
    additionalParams?: AnalyticsEventParams
  ) => {
    trackEvent('menu_item_click', {
      menu_item: menuItem,
      ...additionalParams,
    });
  },

  trackSidebarToggle: (
    state: 'expanded' | 'collapsed',
    additionalParams?: AnalyticsEventParams
  ) => {
    trackEvent('sidebar_toggle', {
      sidebar_state: state,
      ...additionalParams,
    });
  },

  trackSidebarItemClick: (
    sidebarItem: string,
    additionalParams?: AnalyticsEventParams
  ) => {
    trackEvent('sidebar_item_click', {
      sidebar_item: sidebarItem,
      ...additionalParams,
    });
  },

  trackPanelToggle: (
    panelName: string,
    state: 'open' | 'closed',
    additionalParams?: AnalyticsEventParams
  ) => {
    trackEvent('panel_toggle', {
      panel_name: panelName,
      panel_state: state,
      ...additionalParams,
    });
  },

  trackFormSubmit: (
    formName: string,
    formType?: string,
    additionalParams?: AnalyticsEventParams
  ) => {
    trackEvent('form_submit', {
      form_name: formName,
      form_type: formType,
      ...additionalParams,
    });
  },

  trackRoomJoined: (params: {
    room_code: string;
    join_method?: 'form' | 'url';
    user_id?: string;
    [key: string]: string | number | boolean | null | undefined;
  }) => {
    // Set player status for anonymous users
    if (!params.user_id) {
      setPlayerStatus(true);
    }

    // Determine analytics_audience for the event
    const analyticsAudience = params.user_id
      ? undefined // Will be determined from user properties for authenticated users
      : 'player'; // Anonymous users who join are players

    trackEvent('room_joined', {
      ...params,
      analytics_audience: analyticsAudience,
    });
  },

  trackPreferenceChanged: (params: {
    preference_type: string;
    preference_value: string | boolean;
    settings_context?: string;
    action?: 'add' | 'remove';
    onboarding_step?: number;
    [key: string]: string | number | boolean | null | undefined;
  }) => {
    trackEvent('preference_changed', params);
  },

  trackOnboardingStepCompleted: (params: {
    step_number: number;
    step_name: string;
    total_steps: number;
    [key: string]: string | number | boolean | null | undefined;
  }) => {
    trackEvent('onboarding_step_completed', params);
  },

  // General methods
  trackPageView,
  trackEvent,
  setUserId,
  setUserProperties,
  initialize,
};
